/** Sportsbook currency. Create UI: INR | POINTS (1 point = ₹100). POINT@1 is Lab-only parse. */
export const CURRENCY_TYPES = ['INR', 'POINTS', 'POINT@1'] as const
export type CurrencyType = (typeof CURRENCY_TYPES)[number]
export const ASSIGNABLE_CURRENCY_TYPES = ['INR', 'POINTS'] as const
export type AssignableCurrencyType = (typeof ASSIGNABLE_CURRENCY_TYPES)[number]

export function parseCurrencyType(raw: unknown): CurrencyType {
  const s = String(raw ?? '')
    .trim()
    .toUpperCase()
  // POINT@1 treated as POINTS for share + money.
  if (s === 'POINTS' || s === 'POINT' || s === 'POINT@1' || s === 'POINT1' || s === 'POINT_AT_1') {
    return 'POINTS'
  }
  return 'INR'
}

function paisePerDisplay(c: CurrencyType): number {
  if (c === 'POINTS') return 10_000
  if (c === 'POINT@1') return 100
  return 100
}

export function displayToPaise(display: number, currency: CurrencyType): string {
  if (!Number.isFinite(display) || display < 0) return '0'
  return String(Math.round(display * paisePerDisplay(currency)))
}

export function paiseToDisplay(minor: string | number | null | undefined, currency: CurrencyType): number {
  if (minor == null || minor === '') return 0
  const n = typeof minor === 'number' ? minor : Number(minor)
  if (!Number.isFinite(n)) return 0
  return n / paisePerDisplay(currency)
}

/** Order/book INR rupees → agent display (POINTS: 1 pt = ₹100). */
export function inrMajorToDisplay(inrMajor: number, currency: CurrencyType): number {
  if (!Number.isFinite(inrMajor)) return 0
  if (currency === 'POINTS') return inrMajor / 100
  return inrMajor
}

/** Display amount → INR rupees (inverse of inrMajorToDisplay). */
export function displayToInrMajor(display: number, currency: CurrencyType): number {
  if (!Number.isFinite(display) || display < 0) return 0
  if (currency === 'POINTS') return display * 100
  return display
}

/** Re-express a display amount from one Lab currency into another via paise. */
export function convertDisplayAmount(
  amount: number,
  from: CurrencyType,
  to: CurrencyType,
): number {
  if (from === to) return amount
  return paiseToDisplay(displayToPaise(amount, from), to)
}

export function currencyLabel(c: CurrencyType): string {
  if (c === 'POINTS') return 'Points'
  if (c === 'POINT@1') return 'Point@1'
  return 'INR'
}

export function currencyHint(c: CurrencyType): string {
  if (c === 'POINTS') return '1 point = ₹100'
  if (c === 'POINT@1') return '1 point = ₹1'
  return 'Indian rupees'
}

/** CM sets currency on TM or a direct member. TM downline inherits the TM. */
export function canPickCurrency(parentRole: string, childRole: string): boolean {
  return parentRole === 'company-master' && (childRole === 'triple-master' || childRole === 'player')
}

/**
 * Lab create/edit: include casinoCurrencyType only when CM picks TM/member.
 * Mid-level create/edit must omit the field (Agent-API inherits / keeps existing).
 */
export function casinoCurrencyRequestFields(
  parentRole: string,
  childRole: string,
  casinoCurrencyType: 'HKD' | 'AED',
): { casinoCurrencyType: 'HKD' | 'AED' } | Record<string, never> {
  if (!canPickCurrency(parentRole, childRole)) return {}
  return { casinoCurrencyType: casinoCurrencyType === 'AED' ? 'AED' : 'HKD' }
}

export type FundistCurrencyType = 'HKD' | 'AED' | 'USD' | 'SGD' | 'INR'

const FUNDIST_CURRENCIES: FundistCurrencyType[] = ['HKD', 'AED', 'USD', 'SGD', 'INR']

export function parseFundistCurrencyType(
  raw: unknown,
  fallback: FundistCurrencyType = 'HKD',
): FundistCurrencyType {
  const s = String(raw ?? '')
    .trim()
    .toUpperCase()
  if ((FUNDIST_CURRENCIES as string[]).includes(s)) return s as FundistCurrencyType
  return fallback
}

/** Lab create/edit: include fundistCurrencyType only when CM picks TM/member. */
export function fundistCurrencyRequestFields(
  parentRole: string,
  childRole: string,
  fundistCurrencyType: FundistCurrencyType,
): { fundistCurrencyType: FundistCurrencyType } | Record<string, never> {
  if (!canPickCurrency(parentRole, childRole)) return {}
  return { fundistCurrencyType: parseFundistCurrencyType(fundistCurrencyType) }
}

/** Lab create-member: sportsbook currencyType is ng-disabled on edit; casino flag stays editable. */
export function isSportsbookCurrencyLockedOnEdit(
  variant?: 'create' | 'edit',
): boolean {
  return variant === 'edit'
}
